import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { showMessage } from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import { showErrorMessage } from '~/domain/utils';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';

export interface IAdjustHoursPayload {
  jobId: string;
  data: Record<string, unknown>;
  navigateToRating?: boolean;
}

type TAdjustHoursResponse = Record<string, unknown>;
type TAdjustHoursError = AxiosError<{ message?: string; error?: string; title?: string }>;

const ADJUSTMENT_MESSAGE =
  'Your adjustment has been received and sent to the professional for review. Once they confirm this reported time adjustment, all charges related to this shift will be adjusted accordingly.';

/**
 * Adjust-hours mutation for the Job Summary screen. Owns the adjust API request,
 * `adjustment_request` analytics, and the success side effects the saga drove:
 * it bridges the adjusted job id into Redux `adjustmentsSend` (read by
 * JobSummaryRating) and shows the success message whose `route` navigates to the
 * rating screen (or back). Errors surface through the shared error helper.
 */
export function useAdjustHoursMutation() {
  const dispatch = useDispatch();

  return useMutation<TAdjustHoursResponse, TAdjustHoursError, IAdjustHoursPayload>({
    mutationFn: async ({ jobId, data }) => {
      return await apiMethods[apiMethodsConst.ADJUST_HOURS_REQUEST]({ jobId, data });
    },
    onSuccess: (_response, { jobId, data, navigateToRating }) => {
      void logCustomEvent('adjustment_request', { jobId, data });

      // Bridge: record the adjusted job id in Redux (read by JobSummaryRating).
      dispatch({ type: 'ADJUST_HOURS_ACTION_SUCCESS', payload: jobId });

      dispatch(
        showMessage({
          title: 'Adjustment Request Submitted',
          message: ADJUSTMENT_MESSAGE,
          route: navigateToRating ? `/dashboard/job/${jobId}/feedback-rating` : 'back',
          isSuccess: true,
        }),
      );
    },
    onError: (error) => {
      showErrorMessage({ error });
    },
  });
}
